import React from "react";

import { Breadcrumbs, Stack } from "@bookingcom/bui-react";
import { t } from "@bookingcom/lingojs-core";
import { useI18n } from "@bookingcom/lingojs-react";

import { UIFlightSegment, UIOrder } from "@flights/types";
import useCreateUrl from "../../../hooks/useCreateUrl";
import { FilteredLegWithOpenedCheckIn } from "../../../utils/get-filtered-legs-with-opened-checkIn";

import ConfirmationController from "../ConfirmationController";
import { CheckinHeader } from "./CheckinHeader";
import { CheckinHowTo } from "./CheckinHowTo";
import { CheckinSegmentHeader } from "./CheckinSegmentHeader";
import Wrapper from "../../atoms/Wrapper";
import { CheckInInfoCard } from "./CheckinInfoCard";
import { CheckinTimeInfoBanner } from "./CheckinTimeInfoBanner";
import { CheckinFAQs } from "./CheckinFAQs";
import { useTrackCheckinPage } from "../../../utils/experiments/post-booking/flights_web_pb_checkin_page_v2_m2";
import { useTrackCheckInLinks } from "../../../utils/experiments/post-booking/flights_pb_universal_checkin_links_cirium";
import { useTrackLoggedStatusSelfServeActions } from "../../../utils/experiments/post-booking/flights_web_pb_logged_status_self_serve_actions";

interface WebCheckinInnerProps {
  order: UIOrder;
  segmentIndex: number;
  segment: UIFlightSegment;
  filteredLegsWithOpenedCheckIn: FilteredLegWithOpenedCheckIn[];
}

export const WebCheckinInner: React.FC<WebCheckinInnerProps> = ({ order, segment, filteredLegsWithOpenedCheckIn }) => {
  const i18n = useI18n();
  const { createUrl } = useCreateUrl();

  const { isCheckinPageExperimentVariant } = useTrackCheckinPage(order);

  useTrackCheckInLinks(filteredLegsWithOpenedCheckIn.map((legWithOpenedCheckin) => legWithOpenedCheckin.leg));
  useTrackLoggedStatusSelfServeActions(order);

  return (
    <ConfirmationController>
      {() => (
        <>
          <Wrapper noBorder={isCheckinPageExperimentVariant}>
            <Stack gap={4}>
              <Breadcrumbs
                back
                items={[
                  {
                    text: i18n.trans(t("flights_pb_checkin_navigation")),
                    href: createUrl(`/booking/order-details/${order?.orderToken}`)
                  }
                ]}
              />
              <CheckinHeader segment={segment} />
              {!isCheckinPageExperimentVariant && <div />}
            </Stack>
          </Wrapper>
          {isCheckinPageExperimentVariant && (
            <Wrapper style={{ paddingTop: 0 }}>
              <CheckinTimeInfoBanner
                isLessThan24Hours={filteredLegsWithOpenedCheckIn.some(
                  ({ checkinInfoForLeg }) => checkinInfoForLeg.isOnlineCheckinOpen
                )}
              />
            </Wrapper>
          )}
          {!isCheckinPageExperimentVariant && (
            <Wrapper>
              <CheckinHowTo order={order} />
            </Wrapper>
          )}
          <Wrapper>
            <Stack gap={4}>
              <CheckinSegmentHeader
                segment={segment}
                showSubHeader={filteredLegsWithOpenedCheckIn.length === 1}
                showMultiPnrAlert={filteredLegsWithOpenedCheckIn.length > 1}
              />
              {filteredLegsWithOpenedCheckIn.map(({ leg, checkinInfoForLeg }) => {
                const {
                  legIdentifier: { segmentIndex, legIndex },
                  isOnlineCheckinOpen
                } = checkinInfoForLeg;
                return (
                  <CheckInInfoCard
                    key={legIndex}
                    leg={leg}
                    segment={segment}
                    segmentIndex={segmentIndex}
                    legIndex={legIndex}
                    openCheckin={isOnlineCheckinOpen}
                    checkInInfo={checkinInfoForLeg}
                    showHeader={filteredLegsWithOpenedCheckIn.length > 1}
                    showVuelingAlert
                    isUpdatedCheckInPageExperiment={isCheckinPageExperimentVariant}
                  />
                );
              })}
            </Stack>
          </Wrapper>
          {isCheckinPageExperimentVariant && (
            <Wrapper>
              <CheckinFAQs />
            </Wrapper>
          )}
        </>
      )}
    </ConfirmationController>
  );
};
